'use client';

import { Copy } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useEventTracking } from '@/hooks/useEventTracking';
import { EventType } from '@/api/types/events';

interface EmailCopyProps {
  email: string;
  className?: string;
  textClassName?: string;
  iconClassName?: string;
  showIcon?: boolean;
  showOnHover?: boolean;
}

export function EmailCopy({
  email,
  className,
  textClassName,
  iconClassName,
  showIcon = true,
  showOnHover = true,
}: EmailCopyProps) {
  const { toast } = useToast();
  const { trackEvent } = useEventTracking();

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    trackEvent({
      event_type: EventType.CLICK,
      element_id: 'email-copy-inline',
      event_details: {
        action: 'email_copy_click',
        funnel: 'contact_outreach',
        source: 'email_copy_inline',
        email,
      },
    });
    try {
      await navigator.clipboard.writeText(email);
      toast({
        title: 'Email copied to clipboard',
        duration: 2000,
      });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Failed to copy',
        description: 'Please try again',
      });
    }
  };

  return (
    <span className={cn('inline-flex items-center gap-1 group', className)}>
      <button
        type="button"
        className={cn('hover:underline', textClassName)}
        onClick={handleCopy}
        title="Copy email"
      >
        {email}
      </button>
      {showIcon && (
        <button
          type="button"
          onClick={handleCopy}
          className={cn(
            'transition-opacity p-0.5 hover:bg-muted rounded cursor-pointer',
            showOnHover && 'opacity-0 group-hover:opacity-100'
          )}
          title="Copy email"
          aria-label="Copy email"
        >
          <Copy className={cn('text-muted-foreground hover:text-foreground', iconClassName)} />
        </button>
      )}
    </span>
  );
}
